import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Icon from '../../../components/AppIcon';

export default function ThreeModelViewer({ modelUrl }) {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    if (!mountRef.current || !modelUrl) return;

    // 1. Setup Scene, Camera, Renderer
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0a0a0f'); // Dark futuristic background

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 1.5, 4);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    
    mountRef.current.appendChild(renderer.domElement);

    // 2. Add Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 2);
    directionalLight.position.set(5, 10, 7.5);
    scene.add(directionalLight);
    
    // Add a rim light for that 3D pop effect
    const rimLight = new THREE.SpotLight(0x00ffcc, 5);
    rimLight.position.set(-5, 5, -5);
    rimLight.lookAt(0, 0, 0);
    scene.add(rimLight);

    // 3. Setup OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2.0;

    // 4. Load Model
    let mixer; // For animations if any
    const clock = new THREE.Clock();
    const loader = new GLTFLoader();

    loader.load(
      modelUrl,
      (gltf) => {
        setLoading(false);
        const model = gltf.scene;

        // Center and scale the model automatically
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.5 / maxDim; // Fit inside view
        model.scale.set(scale, scale, scale);

        model.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
        
        // Add to a pivot group so rotation centers properly
        const pivot = new THREE.Group();
        pivot.add(model);
        scene.add(pivot);

        // Play animations if present
        if (gltf.animations && gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(model);
          gltf.animations.forEach((clip) => {
            mixer.clipAction(clip).play();
          });
        }
      },
      undefined,
      (err) => {
        console.error("Error loading 3D model:", err);
        setLoading(false);
        setError(true);
      }
    );

    // 5. Intersection Observer for Performance
    // Only animate when the viewer is actually visible on screen
    let isVisible = false;
    let animationFrameId;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isVisible = entry.isIntersecting;
      });
    }, { threshold: 0.1 });
    
    observer.observe(mountRef.current);

    // 6. Animation Loop
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (isVisible) {
        controls.update();
        if (mixer) mixer.update(clock.getDelta());
        renderer.render(scene, camera);
      }
    };
    animate();

    // 7. Handle Resize
    const handleResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    // 8. Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
      
      if (mountRef.current && renderer.domElement) {
        try {
          mountRef.current.removeChild(renderer.domElement);
        } catch(e) {}
      }
      
      renderer.dispose();
      scene.clear();
    };
  }, [modelUrl]);

  return (
    <div className="relative w-full aspect-[4/5] sm:aspect-video rounded-xl overflow-hidden bg-[#0a0a0f] border border-white/5 cursor-grab active:cursor-grabbing group">
      
      {/* 3D Interactive Badge */}
      <div className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-white/10 flex items-center gap-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="material-symbols-outlined text-[#00ffcc] text-[16px] animate-pulse">3d_rotation</span>
        <span className="text-xs font-bold tracking-widest text-[#00ffcc] uppercase">Interactive</span>
      </div>

      <div ref={mountRef} className="w-full h-full" />
      
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0a0a0f]/80 backdrop-blur-sm z-20">
          <span className="animate-spin text-[#00ffcc]"><Icon name="Loader" size={32} /></span>
          <span className="text-xs font-mono text-white/50 tracking-widest uppercase">Loading 3D Asset...</span>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#0a0a0f] z-20">
          <span className="material-symbols-outlined text-red-400 text-3xl">broken_image</span>
          <span className="text-sm text-zinc-500">Failed to load 3D model</span>
        </div>
      )}
      
      {/* Interaction Hint */}
      {!loading && !error && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="px-4 py-2 bg-black/60 backdrop-blur-md rounded-full border border-white/10">
            <span className="text-[10px] tracking-widest text-white/70 uppercase">Drag to rotate • Scroll to zoom</span>
          </div>
        </div>
      )}
    </div>
  );
}
